import _ from "lodash";
import { v4 as uuidv4 } from "uuid"; // For version 5
import { sortBy } from "./collections";
import colors, { textColors } from "../assets/colors";
import {
  IMAGE_URL,
  IMAGE_SIZE_FOR_LIST,
  BIG_IMAGE_SIZE_FOR_DETAIL,
  deliveryPeriodStatuses,
} from "../constants";
import { formatDate } from "./date";

export const requiresMinQuantity = (cluster, collection, sizes, orderType) => {
  return false;

  // previous logic:
  // no min quantity for teamwear
  // if (orderType === "TEAMWEAR") {
  //   return false;
  // }
  //
  // return collection !== "99" && sizes.indexOf("E") > -1 && !isScarf(cluster);
};

export const isScarf = (cluster) => {
  const clusterParts = (cluster ?? "").split("|");
  const lastPart = clusterParts[clusterParts.length - 1];
  return lastPart === "Tücher/Schals";
};

export const colorCode = (textColor) => {
  if (!textColor) return "";
  return String(textColor).split("-")[0]
    ? String(textColor).split("-")[0].trim()
    : null;
};

export const mapProductColorFromColorCode = (code) => `#${colors[code]}`;

export const mapProductTextColorFromColorCode = (code) => textColors[code];

export const mapProductColor = (textColor) => {
  if (!textColor) return "";
  return mapProductColorFromColorCode(colorCode(textColor));
};

export const mapProductTextColor = (textColor) => {
  if (!textColor) return "white";
  return mapProductTextColorFromColorCode(colorCode(textColor));
};

export const sortProductsByNr = (products) =>
  products ? products.sort(sortBy("Nr__c")) : null;

export const imageUrlForList = (imageId) =>
  imageId ? `${IMAGE_URL}${IMAGE_SIZE_FOR_LIST}/${imageId}` : null;

export const bigImageForDetail = (imageId) =>
  imageId ? `${IMAGE_URL}${BIG_IMAGE_SIZE_FOR_DETAIL}/${imageId}` : null;

export const productNr = (product) => {
  if (!product || !product.number) return "";
  return product.number;
};

export const productName = (product) => {
  if (!product || !product.name) return "";
  return product.name.split("-")[0].trim();
};

const formatDeliveryPeriod = (p) =>
  `${formatDate(p.start)} - ${formatDate(p.end)} (${p.id})`;

const sortPeriods = (periods) => _.sortBy(_.compact(periods), (v) => v.start);

export const mapDeliveryPeriods = (periods) => {
  if (!periods) return null;

  const availablePeriods = [];
  const nosPeriods = [];

  periods.forEach((p) => {
    if (!p) {
      return;
    }

    if (p.id.startsWith("NOS")) {
      nosPeriods.push(p);
    } else {
      availablePeriods.push(p);
    }
  });

  return _.uniqBy(
    sortPeriods([].concat(availablePeriods, nosPeriods)).map((p) => ({
      value: p.id,
      label: formatDeliveryPeriod(p),
    })),
    "value",
  );
};

const formatDeliveryPeriodForLetters = (p) =>
  ` ${formatDate(p.s)} - ${formatDate(p.e)}  (${p.c})`;

export const mapDeliveryOptions = (season, options) => {
  const h = [];

  const keys = Object.keys(options);

  keys.forEach((k) => {
    const v = options[k];

    if (v.season !== season) {
      return;
    }

    h.push({
      value: v.date,
      label: v.name,
    });
  });

  const cleanedMap = h.filter((e) => e.value !== undefined);
  return cleanedMap.toSorted((a, b) => a.value.localeCompare(b.value));
};

export const mapDeliveryPeriodsForLetters = (periods) => {
  if (!periods) return null;
  const mappedPeriods = periods.map((p) => {
    if (!p || !p.c) return null;
    return {
      value: p.c,
      label: formatDeliveryPeriodForLetters(p),
      s: p.s,
      e: p.e,
    };
  });

  const availablePeriods = [];
  const nosPeriods = [];

  mappedPeriods.forEach((p) => {
    if (!p) {
      return;
    }

    if (p.label.includes("NOS")) {
      nosPeriods.push(p);
    } else {
      availablePeriods.push(p);
    }
  });

  return [].concat(sortPeriods(availablePeriods), sortPeriods(nosPeriods));
};

export const generateProductId = () => uuidv4();

export const productSeasonCode = (product) => _.get(product, "SeasonCode__c");

const isSearch = (search, number) =>
  !!search && search.length === number && /^\d+$/.test(search);
export const isSearchByEan = (search) => isSearch(search, 13);
export const isSearchByNumber = (search) => isSearch(search, 8);
export const isSearchByMaterialNumber = (search) =>
  isSearch(search, 5) || (!!search && /(\d+)-(\d+)-(\d+)/.test(search));

export const isProductAvailable = (deliveryPeriod) => {
  if (deliveryPeriod === deliveryPeriodStatuses.NOT_AVAILABLE) return false;

  return (
    deliveryPeriod === deliveryPeriodStatuses.AVAILABLE ||
    deliveryPeriod === deliveryPeriodStatuses.CLOSED_BUT_ORDER_AVAILABLE ||
    undefined
  );
};

export const isEanInputDisabled = ({ deliveryPeriodStatus, value }) =>
  !isProductAvailable(deliveryPeriodStatus) || _.isNil(value);

const getClustersForSeason = (products, season) => {
  const seasonProducts = _.values(products[season]);
  const clustersForSeason = seasonProducts.map((k) => k.cl);
  return _.compact(_.uniq(clustersForSeason));
};

const getClustersForSeasons = (seasons, products) =>
  _.uniq(
    _.flatten(
      seasons.map((season) => [...getClustersForSeason(products, season)]),
    ),
  );

export const getAvailableClustersForProducts = (products) => {
  if (!products) return [];

  const seasons = _.keys(products);
  return getClustersForSeasons(seasons, products);
};
